import React, { useEffect, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Divider, Spin } from 'antd';
import { ProfileAvatar } from 'arcl';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import useDebounce from 'Src/common/hooks/useDebounce';
import { JUNE_EVENT_NAME } from 'Src/common/components/mapperV3/constants';
import { trackEvent } from 'Src/common/components/mapperV3/action/index';
import Header from './header';

import './style.scss';

let page = 1;

function MapFromExternalDatabase({ externalDatabaseConfig, handleOk, goBack }) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);
  const [profiles, setProfiles] = useState({ results: [], has_more: false });
  const [selectedProfile, setSelectedProfile] = useState({});
  const [fetchingProfiles, setFetchingProfiles] = useState(false);
  const [currenrPage, setCurrentPage] = useState(page);

  useEffect(() => {
    if (debouncedQuery) {
      setFetchingProfiles(true);
      externalDatabaseConfig
        .onSearch({ query: debouncedQuery, page: 1 })
        .then((res) => {
          setProfiles(res.data);
          setFetchingProfiles(false);
        })
        .catch(() => {
          setProfiles({ results: [], has_more: false });
          setFetchingProfiles(false);
        });
    }
  }, [debouncedQuery]);

  useEffect(() => {
    if (debouncedQuery) {
      setFetchingProfiles(true);
      externalDatabaseConfig
        .onSearch({ query: debouncedQuery, page: currenrPage })
        .then((res) => {
          const newResults = [...profiles.results, ...res.data.results];
          setProfiles({ ...res.data, ...{ results: newResults } });
          setFetchingProfiles(false);
        })
        .catch(() => {
          setProfiles({ results: [], has_more: false });
          setFetchingProfiles(false);
        });
    }
  }, [currenrPage]);

  useEffect(() => () => {
    page = 1;
  });

  function getProfileAlert() {
    if (externalDatabaseConfig?.module === 'giving') {
      return (
        <Alert
          message={
            <p className="arc-color-calendula arc-H150 arc-font-weight-bold">
              <FontAwesomeIcon icon={faTriangleExclamation} className="arc-font-weight-bold" />
              <span className="ml8">This record is not on Almabase</span>
            </p>
          }
          description={
            <div className="arc-color-calendula">
              <p className="arc-p">Donors without records on Almabase</p>
              <p className="profile-list-item">Cannot be reached out through emails outside of this giving page</p>
              <p className="profile-list-item">
                Activities related to the constituent on Almabase cannot pushed to {externalDatabaseConfig.name}
              </p>
            </div>
          }
          type="warning"
          className="mt16"
        />
      );
    }

    return (
      <Alert
        message={
          <p className="arc-color-calendula arc-H150 arc-font-weight-bold">
            <FontAwesomeIcon icon={faTriangleExclamation} className="arc-font-weight-bold" />
            <span className="ml8">This profile is not on Almabase</span>
          </p>
        }
        description={
          <div className="arc-color-calendula">
            <p className="arc-p">Guests without profiles on Almabase</p>
            <p className="profile-list-item">Cannot be reached out through emails outside of this event</p>
            <p className="profile-list-item">
              Activities related to the constituent on Almabase cannot pushed to {externalDatabaseConfig.name}
            </p>
          </div>
        }
        type="warning"
        className="mt16"
      />
    );
  }

  const handleAlumniListingClick = () => {
    trackEvent({
      event: JUNE_EVENT_NAME.alumniProfileListing,
      extra_data: {
        source: 'While creating new profile from an existing RE profile.',
      },
    });
  };

  return (
    <div className="map-external-database-wrapper">
      <Header goBack={goBack} />

      <div className="body">
        <Choose>
          <When condition={isEmpty(selectedProfile)}>
            <p className="arc-H250 arc-color-black mt24">Map constituent on {externalDatabaseConfig.name}</p>
            <p className="arc-H150 mt16 arc-color-gray7">Constituent name or email</p>
            <Input
              placeholder="Enter a name or email"
              className="input-search mt8"
              prefix={<SearchOutlined />}
              onChange={(e) => setQuery(e.target.value)}
            />
            <If condition={profiles.results}>
              <div className="profile-list mt24">
                {profiles.results.map((profile) => (
                  <Button
                    type="link"
                    className="p0 link-btn arc-btn-link profile-list-item-wrapper"
                    onClick={() => setSelectedProfile(profile)}
                    block>
                    <div className="profile-list-item arc-d-flex arc-flex-space-between-align arc-flex-vertical-align mb16">
                      <div style={{ textAlign: 'left' }}>
                        <p className="arc-color-primary arc-p">{profile.name}</p>
                        <If condition={profile.email}>
                          <p className="arc-color-gray9 arc-support">{profile.email}</p>
                        </If>
                        <p className="arc-color-gray7 arc-support">System Record Id: {profile.external_database_id}</p>
                      </div>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="arc-font-weight-bold arc-cursor-p arc-color-primary"
                      />
                    </div>
                    <Divider />
                  </Button>
                ))}
                <If condition={profiles.has_more}>
                  <Button type="link" className="link-btn" onClick={() => setCurrentPage(currenrPage + 1)}>
                    View more
                  </Button>
                </If>
                <If condition={fetchingProfiles}>
                  <div className="arc-d-flex arc-flex-justify-center arc-flex-vertical-align mt16">
                    <Spin />
                  </div>
                </If>
              </div>
            </If>
            <If condition={!fetchingProfiles && isEmpty(profiles.results) && debouncedQuery}>
              <div className="empty-container">
                <img src={`${window.staticPath}img/empty_state.svg`} width={170} alt="Empty search" />
                <p className="arc-H200 arc-color-gray8 arc-font-weight-medium mt16">
                  We could not find that constituent with that name/email
                </p>
              </div>
            </If>
          </When>
          <Otherwise>
            <p className="arc-p arc-color-gray7">On {externalDatabaseConfig.name}:</p>
            <div className="profile-container mt16">
              <p className="arc-color-gray8 arc-p">{selectedProfile.name}</p>
              <If condition={selectedProfile.email}>
                <p className="arc-color-gray7 arc-support">{selectedProfile.email}</p>
              </If>
              <p>
                <span className="arc-H150 arc-color-gray7 arc-font-weight-bold">SYSTEM RECORD ID:</span>
                <span className="arc-H150 arc-color-gray8 arc-font-weight-medium ml4">
                  {selectedProfile.external_database_id}
                </span>
              </p>
            </div>
            <Divider />
            <p className="arc-p arc-color-gray7 mt24">On Almabase:</p>
            <Choose>
              <When condition={isEmpty(selectedProfile.profile)}>
                <p className="mt16 arc-color-gray8 arc-color-H150 arc-font-weight-medium">
                  We couldn&apos;t find a profile that is linked to this system record ID. Do you want to create a
                  profile for this guest on Almabase?
                </p>
                <div className="profile-alert-container">{getProfileAlert()}</div>
                <p className="arc-support arc-d-flex mb4 mt8 arc-color-red7 profile-listing-info">
                  <FontAwesomeIcon className="mr4 mt3" icon={faInfoCircle} />
                  <p>
                    New users will be {window.alumniListingAdminDefault ? 'listed' : 'unlisted'} on the user directory
                    and map. This setting can be edited by{' '}
                    <Button
                      href="/admin/site-settings?tab=custom_features&id=custom_features__alumni_listing_choice__enabled"
                      className="arc-support arc-color-primary link-btn arc-text-underline learn-more-btn"
                      type="link"
                      target="_blank"
                      onClick={handleAlumniListingClick}>
                      <span className="arc-text-underline arc-color-red7">clicking here</span>
                    </Button>
                  </p>
                </p>
                <Button
                  type="primary"
                  className="mt16"
                  block
                  loading={externalDatabaseConfig.creatingProfile}
                  onClick={() => externalDatabaseConfig.onCreateProfile(selectedProfile)}>
                  Create profile and map
                </Button>
              </When>
              <Otherwise>
                <div className="mt16">
                  <ProfileAvatar
                    avatarConfig={{
                      size: 'default',
                      url: selectedProfile?.profile?.present_picture,
                    }}
                    title={selectedProfile?.profile?.full_name}
                    color={window.primaryColor}
                    profileUrl={selectedProfile?.profile?.profile_ur}
                  />
                  <p className="mt16 arc-p arc-color-gray8">
                    We found this profile on Almabase linked to{' '}
                    {selectedProfile?.profile.match_criteria === 'email' ? 'email' : 'system record ID'}. Confirm to map
                    this profile to the guest.
                  </p>
                  <Button type="primary" className="mt16" block onClick={() => handleOk(selectedProfile.profile)}>
                    Confirm
                  </Button>
                </div>
              </Otherwise>
            </Choose>
            <Button
              className="mt8 arc-btn-subtle ignore-mapping-btn"
              block
              onClick={() => externalDatabaseConfig.map(selectedProfile)}>
              Map only to {window.isExternalDatabaseBBCRM ? ' BBCRM' : " Raiser's edge"}
            </Button>
          </Otherwise>
        </Choose>
      </div>
    </div>
  );
}

export default MapFromExternalDatabase;
